import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAllMyGroupsTasks, updateTaskStatus } from '../services/api';
import '../styles/Group.css';

function AllTasks({ user }) {
  const { type } = useParams(); // 'open' או 'in-progress'
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    loadTasks();
  }, [type]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const response = await getAllMyGroupsTasks();
      let filteredTasks = response.data.tasks;
      
      // סינון לפי סוג העמוד
      if (type === 'in-progress') {
        filteredTasks = filteredTasks.filter(t => t.status === 'in-progress');
      }
      
      setTasks(filteredTasks);
      setLoading(false);
    } catch (error) {
      console.error('שגיאה בטעינת משימות:', error);
      setLoading(false);
    }
  };

  const getPageTitle = () => {
    if (type === 'in-progress') return '🔧 משימות בטיפול';
    return '📋 כל המשימות הפתוחות';
  };

  const getDisplayName = (userObj) => {
    if (!userObj) return '';
    return userObj.displayName || userObj.name;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'open': 'פתוח',
      'in-progress': 'בטיפול',
      'completed': 'בוצע',
      'closed': 'סגור'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': '#2196F3',
      'in-progress': '#FF9800',
      'completed': '#4CAF50',
      'closed': '#757575'
    };
    return colors[status] || '#999';
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await updateTaskStatus(taskId, newStatus);
      loadTasks();
    } catch (error) {
      console.error('שגיאה בעדכון סטטוס:', error);
      alert('שגיאה בעדכון סטטוס');
    }
  };

  const openTaskDetails = (task) => {
    setSelectedTask(task);
    setShowTaskDetailsModal(true);
  };

  const getFilteredTasks = () => {
    let filtered = tasks;
    
    // סינון לפי סטטוס (רק אם לא בעמוד "בטיפול")
    if (statusFilter !== 'all' && type !== 'in-progress') {
      filtered = filtered.filter(task => task.status === statusFilter);
    }
    
    // חיפוש טקסט
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(task => 
        (task.taskNumber && task.taskNumber.toLowerCase().includes(query)) ||
        (task.title && task.title.toLowerCase().includes(query)) ||
        (task.location && task.location.toLowerCase().includes(query)) ||
        (task.groupId && task.groupId.name && task.groupId.name.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  };

  if (loading) {
    return <div className="page-loading">טוען...</div>;
  }

  return (
    <div className="group-page">
      {/* Header */}
      <div className="page-header" style={{ borderTop: `4px solid ${type === 'in-progress' ? '#FF9800' : '#667eea'}` }}>
        <div className="page-header-top">
          <h2>{getPageTitle()}</h2>
        </div>
        
        {/* Tabs */}
        <div className="tabs">
          <button className="tab active">
            {type === 'in-progress' ? '🔧' : '📋'} {tasks.length} משימות
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="search-filter-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="חיפוש לפי מספר קריאה, כותרת, מיקום, קבוצה..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>
        
        <div className="filters">
          {type !== 'in-progress' && (
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">כל הסטטוסים</option>
              <option value="open">פתוח</option>
              <option value="in-progress">בטיפול</option>
              <option value="completed">בוצע</option>
            </select>
          )}
          
          {(searchQuery || statusFilter !== 'all') && (
            <button 
              className="clear-filters"
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
              }}
            >
              נקה הכל
            </button>
          )}
        </div>
      </div>

      {/* Tasks List */}
      <div className="tasks-container">
        {getFilteredTasks().length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <p>
              {tasks.length === 0 
                ? 'אין משימות' 
                : 'לא נמצאו משימות התואמות לחיפוש'}
            </p>
          </div>
        ) : (
          getFilteredTasks().map(task => (
            <div 
              key={task._id} 
              className="task-item"
              onClick={() => openTaskDetails(task)}
            >
              <div 
                className={`task-checkbox ${task.status}`}
                onClick={(e) => {
                  e.stopPropagation();
                  const nextStatus = task.status === 'open' ? 'in-progress' : 
                                    task.status === 'in-progress' ? 'completed' : 'open';
                  handleUpdateTaskStatus(task._id, nextStatus);
                }}
              >
                {task.status === 'completed' && '✓'}
              </div>
              
              <div className="task-content">
                <div className="task-title-row">
                  {task.taskNumber && <span className="task-number-badge">#{task.taskNumber}</span>}
                  <h4 className="task-title">
                    {task.title || 'משימה ללא כותרת'}
                  </h4>
                  <span className="task-status-badge" style={{ background: getStatusColor(task.status) }}>
                    {getStatusLabel(task.status)}
                  </span>
                </div>
                
                <div className="task-meta">
                  <span 
                    className="task-meta-item" 
                    style={{ 
                      background: task.groupId.color || '#e7e6ff', 
                      padding: '2px 8px', 
                      borderRadius: '4px',
                      cursor: 'pointer',
                      color: '#fff'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/group/${task.groupId._id}`);
                    }}
                  >
                    📁 {task.groupId.name}
                  </span>
                  {task.location && <span className="task-meta-item">📍 {task.location}</span>}
                  {task.assignedTo && task.assignedTo.length > 0 && (
                    <span className="task-meta-item">
                      👤 {getDisplayName(task.assignedTo[0])}
                      {task.assignedTo.length > 1 && ` +${task.assignedTo.length - 1}`}
                    </span>
                  )}
                </div>
              </div>

              <div className="task-actions" style={{ opacity: 1 }}>
                <select
                  className="status-dropdown"
                  value={task.status}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => handleUpdateTaskStatus(task._id, e.target.value)}
                >
                  <option value="open">פתוח</option>
                  <option value="in-progress">בטיפול</option>
                  <option value="completed">בוצע</option>
                </select>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Task Details Modal */}
      {showTaskDetailsModal && selectedTask && (
        <div className="modal-overlay" onClick={() => { setShowTaskDetailsModal(false); setSelectedTask(null); }}>
          <div className="modal-content modal-task-details" onClick={(e) => e.stopPropagation()}>
            
            {/* Header */}
            <div className="task-modal-header">
              <div className="task-modal-title">
                {selectedTask.taskNumber && <span className="task-number-badge large">#{selectedTask.taskNumber}</span>}
                <h3>{selectedTask.title || 'משימה ללא כותרת'}</h3>
              </div>
              <button className="close-btn" onClick={() => { setShowTaskDetailsModal(false); setSelectedTask(null); }}>✕</button>
            </div>

            {/* Status */}
            <div className="task-modal-status">
              <span className="task-status-badge large" style={{ background: getStatusColor(selectedTask.status) }}>
                {getStatusLabel(selectedTask.status)}
              </span>
              <span 
                style={{ 
                  marginRight: '10px',
                  background: selectedTask.groupId.color || '#e7e6ff', 
                  padding: '5px 14px', 
                  borderRadius: '20px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  color: '#fff'
                }}
                onClick={() => navigate(`/group/${selectedTask.groupId._id}`)}
              >
                📁 {selectedTask.groupId.name}
              </span>
            </div>

            {/* Info Grid */}
            <div className="task-modal-info">
              {selectedTask.description && (
                <div className="info-item">
                  <span className="info-label">📝 תיאור</span>
                  <p className="info-value">{selectedTask.description}</p>
                </div>
              )}
              
              {selectedTask.location && (
                <div className="info-item">
                  <span className="info-label">📍 מיקום</span>
                  <span className="info-value">{selectedTask.location}</span>
                </div>
              )}
              
              {selectedTask.printerModel && (
                <div className="info-item">
                  <span className="info-label">🖨️ דגם</span>
                  <span className="info-value">{selectedTask.printerModel}</span>
                </div>
              )}

              {selectedTask.assignedTo && selectedTask.assignedTo.length > 0 && (
                <div className="info-item">
                  <span className="info-label">👤 משויך ל</span>
                  <span className="info-value">
                    {selectedTask.assignedTo.map(u => getDisplayName(u)).join(', ')}
                  </span>
                </div>
              )}

              <div className="info-item">
                <span className="info-label">📅 נוצר בתאריך</span>
                <span className="info-value">{new Date(selectedTask.createdAt).toLocaleString('he-IL')}</span>
              </div>
            </div>

            {/* Actions */}
            <div style={{ padding: '16px 20px', borderTop: '1px solid #e1dfdd' }}>
              <button 
                className="btn-primary"
                onClick={() => navigate(`/group/${selectedTask.groupId._id}`)}
              >
                🔗 עבור לקבוצה
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

export default AllTasks;